import './PopupContainer.scss'

import { ReactNode, useEffect, useId, useRef } from 'react'

type CloseReason = 'close-button' | 'escape' | 'overlay'

type PopupContainerProps = {
  isOpen: boolean
  title?: string
  subtitle?: string
  children: ReactNode
  footer?: ReactNode
  className?: string
  closeLabel?: string
  closeOnOverlayClick?: boolean
  hideCloseButton?: boolean
  onClose: (reason: CloseReason) => void
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

const BODY_SCROLL_LOCK_COUNT_KEY = 'copPopupScrollLockCount'
const BODY_OVERFLOW_BACKUP_KEY = 'copPopupOverflowBackup'

export default function PopupContainer({
  isOpen,
  title,
  subtitle,
  children,
  footer,
  className = '',
  closeLabel = 'Close',
  closeOnOverlayClick = false,
  hideCloseButton = false,
  onClose,
}: Readonly<PopupContainerProps>) {
  const dialogRef = useRef<HTMLDialogElement | null>(null)
  const onCloseRef = useRef(onClose)
  const titleId = useId()
  const subtitleId = useId()

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!isOpen) return

    const body = document.body
    const currentLockCount = Number(body.dataset[BODY_SCROLL_LOCK_COUNT_KEY] ?? '0')

    if (currentLockCount === 0) {
      body.dataset[BODY_OVERFLOW_BACKUP_KEY] = body.style.overflow || ''
    }

    body.dataset[BODY_SCROLL_LOCK_COUNT_KEY] = String(currentLockCount + 1)
    body.style.overflow = 'hidden'

    const dialogElement = dialogRef.current
    const focusable = dialogElement?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ; (focusable?.[0] ?? dialogElement)?.focus()

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onCloseRef.current('escape')
        return
      }

      if (event.key !== 'Tab') {
        return
      }

      const nodes = dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      if (!nodes || nodes.length === 0) {
        return
      }

      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      const active = document.activeElement as HTMLElement | null

      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
        return
      }

      if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)

      const nextLockCount = Math.max(
        0,
        Number(body.dataset[BODY_SCROLL_LOCK_COUNT_KEY] ?? '1') - 1,
      )

      if (nextLockCount === 0) {
        body.style.overflow = body.dataset[BODY_OVERFLOW_BACKUP_KEY] ?? ''
        delete body.dataset[BODY_SCROLL_LOCK_COUNT_KEY]
        delete body.dataset[BODY_OVERFLOW_BACKUP_KEY]
      } else {
        body.dataset[BODY_SCROLL_LOCK_COUNT_KEY] = String(nextLockCount)
      }
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  return (
    <>
      <div
        className="cop-popup-container__overlay"
        onMouseDown={event => {
          if (!closeOnOverlayClick) return
          if (event.target === event.currentTarget) {
            onClose('overlay')
          }
        }}
        aria-hidden="true"
      />
      <dialog
        ref={dialogRef}
        className={`cop-popup-container ${className}`.trim()}
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-describedby={subtitle ? subtitleId : undefined}
        open={isOpen}
      >
        {(title || subtitle || !hideCloseButton) && (
          <header className="cop-popup-container__header">
            <div className="cop-popup-container__heading">
              {title && (
                <h2 id={titleId} className="cop-popup-container__title">{title}</h2>
              )}
              {subtitle && <p id={subtitleId} className="cop-popup-container__subtitle">{subtitle}</p>}
            </div>
            {!hideCloseButton && (
              <button
                type="button"
                className="cop-popup-container__close"
                aria-label={closeLabel}
                onClick={() => onClose('close-button')}
              >
                &#x2715;
              </button>
            )}
          </header>
        )}

        <div className="cop-popup-container__body">{children}</div>

        {footer && <footer className="cop-popup-container__footer">{footer}</footer>}
      </dialog>
    </>
  )
}
